import classNames from 'classnames';
import _ from 'lodash';
import {
  SelectionAreaWrapper,
  NoBeatsDataReviewedIcon as noBeatsDataReviewdIcon,
  SelectCellHeatmapIcon as selectCellHeatmapIcon,
} from '@/shared/ui';
import { ID_DIV_STRIP_BEATS } from '@/widgets/beats/ui/helper';
import { hasTemplateBeat } from '@/widgets/beats/ui/reviewBeatsHelper';
import { LOAD_BEAT_STATUS } from '@/shared/model';
import BeatPreviewStrip from './beatPreviewStrip';
import useStripBeats from './useStripBeats';

const beatPreviewStripOffset = 2000;

export type StripBeatsProps = {
  ecgDataMap: any;
  studyId: string;
  profileId: string;
  timezoneOffset: number;
  callbackListDate: (listDate: any[]) => void;
};

/**
 * Renders the scrollable list of beat thumbnails for the cells currently picked on the R-R heatmap. Prompts the reviewer to select a cell while the beat load status is NONE, shows an everything-reviewed message when a selection yields no beats, and otherwise wraps BeatPreviewStrip items in a drag-selection area seeded with the active strip id.
 *
 * @param props - Strip list inputs including the ECG data map, study and profile ids, timezone offset and the date-list callback.
 * @returns The selectable beat strip list, or a guidance message when there is nothing to show.
 */
const StripBeats = ({
  ecgDataMap,
  studyId,
  profileId,
  timezoneOffset,
  callbackListDate,
}: StripBeatsProps) => {
  const {
    loadingBeatStatus,
    activeButton,
    listSelectedCells,
    selectedTemplate,
    holterBeats,
    isLoadingStrip,
    selectionAreaWrapperRef,
    setSelectedBeat,
    selectedStrip,
    gainThumbnail,
    channelsThumbnail,
    sizeThumbnailConfig,
    highPassThumbnail,
    handleClickSelectBeat,
  } = useStripBeats({ studyId, profileId, timezoneOffset, callbackListDate });

  if (loadingBeatStatus.status === LOAD_BEAT_STATUS.NONE) {
    return (
      <div id={ID_DIV_STRIP_BEATS} className="no-cell-selected">
        <img src={selectCellHeatmapIcon} alt="select cell on heat map" />
        {`Select a cell in the heatmap below ${hasTemplateBeat(activeButton) ? 'or a morphology from the list on the right ' : ''}to view its beats.`}
      </div>
    );
  }
  const showNoBeatsMessage =
    !_.isEmpty(listSelectedCells) ||
    (hasTemplateBeat(activeButton) && !_.isEmpty(selectedTemplate));
  if (holterBeats?.length === 0 && showNoBeatsMessage && !isLoadingStrip) {
    return (
      <div id={ID_DIV_STRIP_BEATS} className="no-cell-selected">
        <img src={noBeatsDataReviewdIcon} alt="no beats data reviewed" />
        {
          'Looks like all beats have already been reviewed. \nYou can change the filter above to display reviewed beats.'
        }
      </div>
    );
  }

  return (
    <SelectionAreaWrapper
      ref={selectionAreaWrapperRef}
      id={ID_DIV_STRIP_BEATS}
      className="strips-container --min-width-strip-rr-heat-map"
      selectables=".single-strip-beat"
      handleSetSelected={setSelectedBeat}
      defaultSelectedId={selectedStrip?.id as any}
      isNoData={holterBeats?.length === 0}
      isLoading={isLoadingStrip}
    >
      {(selected) => (
        <div className={classNames('strips-img-container rr-heat-map-beats')}>
          {_.map(holterBeats, (beat, index) => (
            <BeatPreviewStrip
              key={beat.id}
              index={index}
              id={`beat-${beat.id}`}
              isActive={beat?.id === selectedStrip?.id}
              isSelected={selected.has(`beat-${beat.id}`)}
              beat={beat}
              gain={gainThumbnail}
              channels={channelsThumbnail}
              width={sizeThumbnailConfig.width}
              height={sizeThumbnailConfig.height}
              ecgDataMap={ecgDataMap}
              highPass={highPassThumbnail}
              offset={beatPreviewStripOffset}
              onClickSelectBeat={handleClickSelectBeat}
              activeButton={activeButton}
            />
          ))}
        </div>
      )}
    </SelectionAreaWrapper>
  );
};

export default StripBeats;
